import React, { Component, PropTypes } from 'react'

const FIVE_MEBIBYTES = 5242880

class ProgressKeeper {
    data = {}

    constructor(data = {}) {
        this.data = data
    }

    add(id) {
        id = String(id)

        let data = { ...this.data }

        // eslint-disable-next-line no-magic-numbers
        if (Object.values(data).every((item) => item === 100)) {
            data = {}
        }

        data[id] = 0

        return new ProgressKeeper(data)
    }

    remove(id) {
        id = String(id)

        const data = { ...this.data }

        delete data[id]

        return new ProgressKeeper(data)
    }

    update(id, value) {
        id = String(id)

        if (Object.keys(this.data).includes(id)) {
            const data = { ...this.data, [id]: value }

            return new ProgressKeeper(data)
        }

        return this
    }

    get value() {
        const values = Object.values(this.data)

        return values.reduce((acc, item) => acc + item, 0) / values.length
    }

    get length() {
        return Object.values(this.data).length
    }

    get inProgress() {
        // eslint-disable-next-line no-magic-numbers
        return this.length > 0 && this.value < 100
    }

    get completed() {
        const values = Object.values(this.data)

        // eslint-disable-next-line no-magic-numbers
        return values.filter((item) => item === 100).length
    }
}

class FileUpload extends Component {
    static propTypes = {
        data: PropTypes.object,
        error: PropTypes.array,
        errorAction: PropTypes.func.isRequired,
        helpText: PropTypes.node,
        multiple: PropTypes.bool,
        name: PropTypes.string.isRequired,
        updateValue: PropTypes.func.isRequired,
        uploadAction: PropTypes.func.isRequired,
        value: PropTypes.string,
    }

    static defaultProps = {
        data: {},
        error: [],
        helpText: 'Please upload the file in PDF format. The file size must not exceed 5MB.',
        multiple: false,
        value: null,
    }

    state = {
        progress: new ProgressKeeper(),
    }

    counter = 0

    _onFileChange = (e) => {
        if (e.target.files.length) {
            const files = Array.from(e.target.files)

            if (this.props.multiple) {
                // Ensure that all files are lte 5MiB
                if (files.every(
                    (file) => file.size <= FIVE_MEBIBYTES
                )) {
                    // If all good, commence upload for each.
                    for (const file of files) {
                        this.counter += 1
                        this._uploadOne(this.counter, file)
                    }
                } else {
                    // If at least one was too large, cancel the whole thing off and say something went wrong.
                    this.props.errorAction({ [ this.props.name ]: [ 'All files must be less than 5MB' ] })
                }

            } else {
                const file = files[0]

                if (file.size > FIVE_MEBIBYTES) {
                    this.props.errorAction({ [ this.props.name ]: [ 'The file size must be less than 5MB' ] })
                } else {
                    this.counter += 1
                    this._uploadOne(this.counter, file)
                }
            }
        }
    }

    _uploadOne(id, file) {
        this.setState((state) => {
            return {
                progress: state.progress.add(id, file.name),
            }
        })

        this.props.uploadAction(
            file,
            this.props.data,
            (value) => {
                // When upload is finished set this field's value to the value returned by the API.
                this.props.updateValue({ [this.props.name]: value })

                // And ensure we set this file's percentage to 100%.
                this.setState((state) => {
                    return {
                        // eslint-disable-next-line no-magic-numbers
                        progress: state.progress.update(id, 100),
                    }
                })
            },
            (progress) => {
                this.setState((state) => {
                    return {
                        progress: state.progress.update(id, progress),
                    }
                })
            }
        )
    }

    render() {
        let errors = null

        if (this.props.error && this.props.error.length) {
            errors = (
                <div className="alert alert--error">
                    { this.props.error.map(
                        (error, index) => <span key={ index }>{ error }<br /></span>
                    ) }
                </div>
            )
        }

        let content = null

        if (this.props.value) {
            content = (
                <a
                    className="btn btn--primary"
                    href={ this.props.value }
                    target="_blank"
                >
                    Download file
                </a>
            )
        } else {
            if (this.state.progress.inProgress) {
                const style = {
                    // eslint-disable-next-line no-magic-numbers
                    width: `${ Math.min(100, this.state.progress.value) }%`,
                }

                content = (
                    <div>
                        Uploading, { this.state.progress.completed } / { this.state.progress.length } completed...

                        <div className="progress-bar">
                            <div className="progress-bar__meter" style={ style } />
                        </div>
                    </div>
                )
            }
        }

        return (
            <div>
                { errors }
                { this.props.helpText }
                { content }

                <input
                    accept="application/pdf,image/png,image/jpeg"
                    multiple={ this.props.multiple }
                    onChange={ this._onFileChange }
                    type="file"
                />
            </div>
        )
    }
}

export default FileUpload



// WEBPACK FOOTER //
// ./app/components/ui/file_upload.js